import { Injectable } from '@angular/core';

import * as dicomParser from 'dicom-parser';
import * as dcmjs from 'dcmjs';  // npm install dcmjs

// Services
import { UserAuthenticationService } from '../firestore/user-authentication.service';
import { HttpService } from '../http/http.service';

// Cloud Run strings (environment dependent)
import { environment } from '../../../environments/environment';
import { GlobalVariables } from '../../constants/global';


import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
// Cornerstone initialization
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;



@Injectable({
  providedIn: 'root'
})
export class DicomService {


  private _dicomKeysToKeep = [
    '_vrMap',
    'SOPClassUID', 
    'SOPInstanceUID', 
    'TransferSyntaxUID',
    
    // US Processing
    'SamplesPerPixel', 
    'PhotometricInterpretation', 
    'Rows', 
    'Columns',
    'PixelData', 
    'BitsAllocated', 
    'BitsStored', 
    'HighBit', 
    'PixelRepresentation',
    'WindowCenter', 
    'WindowWidth',
    'SequenceOfUltrasoundRegions', 
    'PlanarConfiguration'
  ]


  constructor(
    public userAuthenticationService:UserAuthenticationService,
    public httpService:HttpService,
  ) { }

  async loadNewFile(file:File){
    // Read the file asynchronously and wait for it to finish
    let dataSet = await this.readFileAsync(file)  
    if(dataSet === undefined) return {resultOk: false, error: 'Error! Failed to read the file. It may be corrupted.'}
    // Verify that the image modality is US. Type 2 of DICOM images does not work
    // let imageModality = dataSet.string('x00080060') 
    // if (imageModality !== 'US') return {resultOk: false, error: 'Error! The selected file does not contain an ultrasound DICOM image.'}
    // Anonymize the DICOM asynchronously and wait for it to finish
    let blob = await this.anonymizeDicomFile(file)
    // Upload the anonymised DICOM and wait for it to finish
    let resultsDict = await this.uploadDicom(blob)
    // Return the results
    return resultsDict
  }

  
  // Read a file asynchronously and return the promise. On completion, it returns a DICOM dataset
  readFileAsync(file:File): Promise<dicomParser.DataSet | undefined>  {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => {
        try{
          let arrayBuffer = reader.result as ArrayBuffer;
          let byteArray = new Uint8Array(arrayBuffer);
          let dataSet:dicomParser.DataSet = dicomParser.parseDicom(byteArray);
          resolve(dataSet);
        }catch{
          resolve(undefined)
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    })
  }


  // Anonymize a DICOM and return the Blob
  async anonymizeDicomFile(file:File){
    // Read the file asynchronously and wait for it to finish
    let arrayBuffer = await this.readFileArrayAsync(file)
    let dicomData = dcmjs.data.DicomMessage.readFile(arrayBuffer);
    let dataSet = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dicomData.dict);
    // Replace some attributes and delete others
    for(let key of Object.keys(dataSet)){
      if(key == 'StudyInstanceUID') dataSet[key] = 'study'
      else if(key == 'SeriesInstanceUID') dataSet[key] = 'series'
      else if(key == 'SOPInstanceUID') dataSet[key] = 'image'
      else if(!this._dicomKeysToKeep.includes(key)) delete dataSet[key]
    }
    // Store the new DICOM data
    dicomData.dict = dcmjs.data.DicomMetaDictionary.denaturalizeDataset(dataSet);
    let newArrayBuffer = dicomData.write();
    // Create the Blob from the DICOM byte array
    let blob = new Blob([newArrayBuffer], {type: "application/dicom"});
    return blob
  }


  // Read a file asynchronously and return the promise. On completion, it returns an array
  readFileArrayAsync(file): Promise<any>  {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => {
        try{
          let arrayBuffer = reader.result as ArrayBuffer;
          resolve(arrayBuffer);
        }catch{
          resolve(undefined)
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    })
  }


  // Upload the DICOM image using the Cloud Run uploadFile
  async uploadDicom(blob: Blob): Promise<any | undefined>{
    const userIdToken: string = await this.userAuthenticationService.getUserIdToken();
    const urlString: string = environment.googleCloudRun.uploadImage;
    const userId = this.userAuthenticationService.getUserId().toString();
    
    let formData: FormData = new FormData();
    formData.append('userId', userId);
    formData.append('bucketName', userId);
    formData.append('file', blob);

    let resultDict = await this.httpService.executeHttpPostMethodSigned(userIdToken, urlString, formData);
    
    return resultDict;
  }

  // Send to classify the DICOM image using the Cloud Run classify
  async classifyDicom(): Promise<any | undefined>{
    const userIdToken: string = await this.userAuthenticationService.getUserIdToken();
    const urlString: string = environment.googleCloudRun.classify;
    const userId = this.userAuthenticationService.getUserId().toString();
    
    let formData: FormData = new FormData();
    formData.append('userId', userId);
    formData.append('bucketName', userId);

    let resultDict = await this.httpService.executeHttpPostMethodSigned(userIdToken, urlString, formData);
    
    return resultDict;
  }

  // Update or modify class map
  async modifyClassMap(classMapBin, imageWidth:number, imageHeigth:number, callback: (data: any) => void){
    // Create Blob of classMapBin
    let classMapBinBlob = new Blob([classMapBin], {type: 'application/octet-stream'});

    const userIdToken: string = await this.userAuthenticationService.getUserIdToken();
    const urlString: string = environment.googleCloudRun.modify;
    const userId = this.userAuthenticationService.getUserId().toString();
    
    let formData = new FormData();
    formData.append('userId', userId)
    formData.append('bucketName', userId);
    formData.append('imageWidth', imageWidth.toString())
    formData.append('imageHeigth', imageHeigth.toString())
    
    let colorList = GlobalVariables.batscan.classes.map(c => c.color)
    formData.append('classColors', JSON.stringify(colorList))
    
    formData.append('classMapBinFile', classMapBinBlob)
    
    let resultDict = await this.httpService.executeHttpPostMethodSigned(userIdToken, urlString, formData)
    
    return callback(resultDict);
  }

  async generateResults(patientId, studyId, additionalInfoDict, callback: (data: any) => void){
    const userIdToken: string = await this.userAuthenticationService.getUserIdToken();
    const urlString: string = environment.googleCloudRun.results;
    const userId = this.userAuthenticationService.getUserId().toString();
    
    let formData = new FormData();
    formData.append('userId', userId)
    formData.append('bucketName', userId);
    formData.append('patientId', patientId);
    formData.append('studyId', studyId);
    formData.append('additionalInformationDict', JSON.stringify(additionalInfoDict));
    
    let resultDict = await this.httpService.executeHttpPostMethodSigned(userIdToken, urlString, formData)
    
    return callback(resultDict);
  }
}
